import { useCallback, useMemo } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';

export interface CompactNumberFormatOptions extends Omit<Intl.NumberFormatOptions, 'notation' | 'compactDisplay'> {
  compactDisplay?: 'short' | 'long';
}

/**
 * Hook for formatting numbers in a compact way (e.g., 1000 -> 1K) using the current locale.
 *
 * @returns {Object} Formatter object
 * @returns {(number: number) => string} formatCompactNumber - Function to format a number into a compact string
 * @returns {string} locale - The current locale being used for formatting
 *
 * @example
 * const { formatCompactNumber } = useCompactNumberFormatter();
 * const result = formatCompactNumber(1000); // "1K"
 * const result2 = formatCompactNumber(1000000); // "1M"
 */
export const useCompactNumberFormatter = () => {
  const { language } = useLocaleContext();

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        notation: 'compact',
        compactDisplay: 'short'
      }),
    [language]
  );

  const formatCompactNumber = useCallback((number: number) => formatter.format(number), [formatter]);

  return {
    formatCompactNumber,
    locale: language
  };
};
